import { useMemo } from "react";
import { parseMoodTimestamp } from "utils/common/timestampUtils";
import { isSameDay } from "utils/common/weekUtils";
import { MOOD_COLORS } from "constants/moodColors";
import { getWeekdayDates } from "./weekdayUtils";
import type { Mood } from "../../../types/models";
import type { WeekInfo } from "./useWeekManagement";

export interface BarChartSegment {
  color: string;
  height: number;
}

export interface BarChartDataItem {
  totalHeight: number;
  segments: BarChartSegment[];
}

const fixedHeight = 60;

const MOOD_ORDER = ["Apathy", "Flow", "Anxiety", "Confident"] as const;

function barItemFromMoods(dayMoods: Mood[]): BarChartDataItem {
  const moodDurations: Record<string, number> = {
    Apathy: 0,
    Flow: 0,
    Anxiety: 0,
    Confident: 0,
  };

  dayMoods.forEach((mood: Mood) => {
    const duration = parseInt(String(mood.duration)) || 1;
    const moodType = mood.mood;
    if (Object.prototype.hasOwnProperty.call(moodDurations, moodType)) {
      moodDurations[moodType] += duration;
    }
  });

  const totalDuration = Object.values(moodDurations).reduce(
    (sum, val) => sum + val,
    0
  );

  if (totalDuration === 0) {
    return { totalHeight: fixedHeight, segments: [] };
  }

  const segments: Array<{ color: string; height: number; mood: string }> = [];

  MOOD_ORDER.forEach((moodType) => {
    const duration = moodDurations[moodType];
    if (duration > 0) {
      const proportion = duration / totalDuration;
      const height = Math.max(2, Math.round(fixedHeight * proportion));
      segments.push({
        color: MOOD_COLORS[moodType as keyof typeof MOOD_COLORS],
        height,
        mood: moodType,
      });
    }
  });

  const currentTotal = segments.reduce((sum, seg) => sum + seg.height, 0);
  if (currentTotal !== fixedHeight && segments.length > 0) {
    const diff = fixedHeight - currentTotal;
    segments[segments.length - 1].height += diff;
    if (segments[segments.length - 1].height < 2) {
      segments[segments.length - 1].height = 2;
    }
  }

  return {
    totalHeight: fixedHeight,
    segments: segments.map(({ color, height }) => ({ color, height })),
  };
}

function addUtcDays(d: Date, n: number): Date {
  const x = new Date(d.getTime());
  x.setUTCDate(x.getUTCDate() + n);
  return x;
}

function mondayOfUtcWeekContaining(date: Date): Date {
  const d = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  );
  const dow = d.getUTCDay();
  const offset = dow === 0 ? -6 : 1 - dow;
  d.setUTCDate(d.getUTCDate() + offset);
  return d;
}

/** Each entry is Mon–Fri (UTC) for one work-week that overlaps the calendar month */
export function getUtcWorkWeeksOverlappingMonth(monthKey: string): Date[][] {
  const parts = monthKey.split("-").map(Number);
  if (parts.length < 2 || !monthKey) return [];
  const year = parts[0];
  const month1to12 = parts[1];
  if (!year || month1to12 < 1 || month1to12 > 12) return [];

  const first = new Date(Date.UTC(year, month1to12 - 1, 1));
  const last = new Date(Date.UTC(year, month1to12, 0));

  let monday = mondayOfUtcWeekContaining(first);
  const rows: Date[][] = [];

  for (;;) {
    const fri = addUtcDays(monday, 4);
    if (fri < first) {
      monday = addUtcDays(monday, 7);
      continue;
    }
    if (monday > last) break;

    const weekDays: Date[] = [];
    for (let i = 0; i < 5; i++) {
      weekDays.push(addUtcDays(monday, i));
    }
    rows.push(weekDays);
    monday = addUtcDays(monday, 7);
  }

  return rows;
}

export function useBarChartData(
  moods: Mood[],
  selectedView: "PERSONAL" | "TEAM",
  selectedWeekData: WeekInfo | null,
  availableWeeks: WeekInfo[],
  lastEndedWeek: Date[],
  insightsGranularity: "week" | "month" = "week",
  selectedMonthKey: string = ""
): BarChartDataItem[][] {
  const barChartRows = useMemo(() => {
    if (insightsGranularity === "month") {
      const weeks = getUtcWorkWeeksOverlappingMonth(selectedMonthKey);
      if (weeks.length === 0) return [];
      return weeks.map((weekDays) =>
        weekDays.map((weekdayDate) => {
          const dayMoods = moods.filter((mood: Mood) => {
            const moodDate = parseMoodTimestamp(mood);
            if (!moodDate) return false;
            return isSameDay(moodDate, weekdayDate);
          });
          return barItemFromMoods(dayMoods);
        })
      );
    }

    const weekdayDates = getWeekdayDates(
      selectedView,
      selectedWeekData,
      availableWeeks,
      lastEndedWeek
    );

    const row = weekdayDates.map((weekdayDate) => {
      const dayMoods = moods.filter((mood: Mood) => {
        const moodDate = parseMoodTimestamp(mood);
        if (!moodDate) return false;
        return isSameDay(moodDate, weekdayDate);
      });
      return barItemFromMoods(dayMoods);
    });
    if (row.length === 0) return [];
    return [row];
  }, [
    moods,
    selectedView,
    selectedWeekData,
    availableWeeks,
    lastEndedWeek,
    insightsGranularity,
    selectedMonthKey,
  ]);

  return barChartRows;
}
